<template>
  <!-- Model patch matcher -->
  <v-row class="my-0 pt-4 pb-0">
    <v-col cols="2" class="py-1 pr-0">{{ $t("modelPathMatcher") }}: </v-col>
    <v-col cols="10" class="py-1 pr-0">
      <div class="radio-container mx-4" @click="toggleModelPathMatcher(TriggerPolicyModelPathMatcherEnum.SIMPLE)">
        <v-icon 
          :color="modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.SIMPLE ? 'var(--color-highlight)' : 'var(--color-g4)'"
          style="font-size:22px">{{modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.SIMPLE  ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('simple') }}</div>
      </div>
      <div class="radio-container" @click="toggleModelPathMatcher(TriggerPolicyModelPathMatcherEnum.ADVANCED)">
        <v-icon 
          :color="modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.ADVANCED ? 'var(--color-highlight)' : 'var(--color-g4)'"
          style="font-size:22px">{{modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.ADVANCED ? 'ecc:ecc-E-RadioON' : 'ecc:ecc-E-RadioOFF'}}</v-icon>
        <div class="radio-text">{{ $t('advanced') }}</div>
      </div>
    </v-col>
  </v-row>
  <!-- Simple mode -->
  <v-row class="py-0 mt-0 mb-3" v-if="modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.SIMPLE">
    <v-col class="my-0 pb-0 pt-1 trigger-model-path-matcher">
      <v-row class="my-0 py-0">
        <!-- Model -->
        <v-col class="py-0">
          <v-autocomplete
            v-model="deviceSlotModel"
            :loading="loadingDeviceModels"
            :items="models"
            :return-object="true"
            :item-title="deviceSlotModelTitle"
            :label="$t('model')"
            @click:clear="clear"
            @change="deviceSlotModelChangeByUser"
            @update:search="search"
            :no-data-text="$t('noModelFound', { value: searchDeviceModels })"
            clearable
          >
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props" :title="item.raw.version ? item.raw.name+':'+item.raw.version : item.raw.name"></v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
      <v-row class="my-0 py-0">
        <!-- Tag -->
        <v-col class="py-0">
          <v-autocomplete
            v-model="deviceSlotModelTag"
            :loading="loadingDeviceModelsTag"
            :items="computedTagByModel"
            :item-title="deviceSlotModelTagText"
            :return-object="true"
            :label="$t('tag')"
            :no-data-text="$t('noTagFound')"
            clearable
          >
            <!-- dropdown item -->
            <template v-slot:item="{ props, item }">
              <v-list-item v-bind="props">
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.DOUBLE"
                  >ecc:ecc-A-Double</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.INTEGER"
                  >ecc:ecc-A-Number</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.BOOLEAN"
                  >ecc:ecc-A-Boolean</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.STRING"
                  >ecc:ecc-A-String</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == 'object'"
                  >ecc:ecc-A-object</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == 'struct'"
                  >ecc:ecc-A-Structure</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.DOUBLEARRAY"
                  >ecc:ecc-A-ArrayD</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.INTEGERARRAY"
                  >ecc:ecc-A-ArrayI</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.BOOLEANARRAY"
                  >ecc:ecc-A-ArrayB</v-icon
                >
                <v-icon
                  class="col-g4 inline mr-2"
                  v-if="item.value.type == ModelTypeEnum.BINARYBLOB"
                  >ecc:ecc-A-Blob</v-icon
                >
                {{ item.name }}
              </v-list-item>
            </template>
          </v-autocomplete>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
  <!-- Advanced mode -->
  <v-row v-else class="mt-0 mb-3">
    <v-col class="py-0">
      <v-text-field
        persistent-hint
        required
        data-qa="model-path-matcher-string"
        v-model="policy.modelPathMatcher"
        :hint="$t('modelPathMatcherRules')"
        :label="$t('modelPathMatcherString')" 
      />
    </v-col>
  </v-row>
</template>

<script lang="ts">
import { dataStore } from "../../stores/data";
import AnimatedButton from "../UIComponents/AnimatedButton.vue";
import { defineComponent } from "vue";
import {
  IModelRequestDTO,
  ModelTypeEnum,
  TriggerPolicyAnalogBand,
  TriggerPolicyModelPathMatcherEnum,
  TriggerPolicyOnChange,
  TriggerPolicyOnLevel,
} from "./type.util";
import { debounce } from "lodash";

export default defineComponent({
  name: "TriggerComponentModelPathMatcher",
  components: {
    AnimatedButton,
  },
  data() {
    return {
      dataStore: dataStore(),
      TriggerPolicyModelPathMatcherEnum: TriggerPolicyModelPathMatcherEnum,
      ModelTypeEnum: ModelTypeEnum,

      deviceSlotModel: null,
      deviceSlotModelTag: null,

      deviceSlotModelsAndSubModels: [],

      infiniteIdModels: +new Date(),
      infiniteIdModelsTag: +new Date(),

      loadingDeviceModels: false,
      loadingDeviceModelsTag: false,

      models: [],
      searchDeviceModels: null,
      defaulPageSize: 20,
      modelsPage: 0,
      modelsTagPage: 0,
      lastModelState: null,
      lastModelStateTag: null,
      debouncedFetchModelsWithOptions: null,

      modelPathMatcherMode: TriggerPolicyModelPathMatcherEnum.SIMPLE,
    };
  },
  props: {
    policy: {
      type: Object as () =>
        | TriggerPolicyAnalogBand
        | TriggerPolicyOnChange
        | TriggerPolicyOnLevel,
      default: null,
    },
  },
  computed: {
    computedTagByModel(): Array<any> {
      if (this.deviceSlotModel) {
        const properties = this.deviceSlotModel.json?.properties || this.deviceSlotModel.properties;
        const tags = this.convertObjectIntoArray(properties).filter((tag) => !tag.deprecated && tag.type !== "object");
        return tags;
      } else {
        return [];
      }
    },
    isValid(): boolean {
      if (this.deviceSlotModel && this.deviceSlotModelTag) {
        return true;
      }
      return false;
    },
  },
  methods: {
    deviceSlotModelChangeByUser() {
      this.deviceSlotModelTag = null;
    },
    clear() {
      this.deviceSlotModel = null;
      this.deviceSlotModelTag = null;
      this.deviceSlotModelsAndSubModels = [];
      this.infiniteIdModels = +new Date();
      this.infiniteIdModelsTag = +new Date();
      this.loadingDeviceModels = false;
      this.loadingDeviceModelsTag = false;
      this.models = [];
      this.searchDeviceModels = null;
      this.defaulPageSize = 10;
      this.modelsPage = 0;
      this.modelsTagPage = 0;
      this.lastModelState = null;
      this.lastModelStateTag = null;
      this.debouncedFetchModelsWithOptions.cancel();
      //this.modelPathMatcherMode=TriggerPolicyModelPathMatcherEnum.ADVANCED;
      this.fetchModelsWithOptions();
    },
    convertObjectIntoArray(obj) {
      const array = [];
      for (let key in obj) {
        obj[key].name = key;
        obj[key].version = null;
        array.push(obj[key]);
      }
      return array;
    },
    deviceSlotModelTitle(item): string {
      if (this.deviceSlotModel) {
        const searches = this.searchDeviceModels?.split("/");
        if (searches?.length > 1) {
          searches.pop();
          searches.push(item.name);
          return searches.join("/")
        }
      }
      return item.name + ":" + item.version;
    },
    deviceSlotModelTagText(item): string {
      return item.name;
    },
    async fetchModelsWithOptions(search: string = "") {
      this.loadingDeviceModels = true;
      let name = "";
      let version = "";
      const param: IModelRequestDTO = { pageSize: this.defaulPageSize, orderBy: "name", page: this.modelsPage };
      let stringSearch = search || "";
      const searches = stringSearch.split("/");
      if (searches.length > 1) {
        const modelFound = searches[0];
        [ name, version ] = modelFound.split(":");
      } else {
        if (stringSearch.includes(":")) {
          stringSearch = stringSearch.split(":")[0];
        }
      }
      if (name) {
        param.name = name;
      } else {
        param.search = stringSearch;
      }

      if (version) param.version = version;
      const result = await this.dataStore.fetchModels(param);
      this.loadingDeviceModels = false;
      let data = result.data;
       if (name && version) {
        let submodels = data.length > 0 ? this.convertObjectIntoArray(data[0].json.properties).filter((model) => model.type == "object" && !model.deprecated) : [];
        for (let i = 1; i < searches.length-1; i+=1 ) {
          const subModel = submodels.find((model) => model.name == searches[i]);
          submodels = subModel ? this.convertObjectIntoArray(subModel.properties).filter((model) => model.type == "object" && !model.deprecated) : [];
        }
        data = submodels;
      }

      if (this.modelsPage == 0) {
        this.models = data;
      } else {
        this.models.push(...data);
      }
      return result;
    },
    async initPolicy(policy) {
      this.deviceSlotModel = null;
      this.deviceSlotModelTag = null;
      this.infiniteIdModels = +new Date();
      this.infiniteIdModelsTag = +new Date();
      this.loadingDeviceModels = false;
      this.loadingDeviceModelsTag = false;
      this.models = [] as any[];
      this.searchDeviceModels = null;
      this.defaulPageSize = 10;

      this.modelsPage = 0;
      this.modelsTagPage = 0;
      this.lastModelState = null;
      this.lastModelStateTag = null;
      this.debouncedFetchModelsWithOptions.cancel();
      this.debouncedFetchModelsWithOptions = null;
      this.modelPathMatcherMode = TriggerPolicyModelPathMatcherEnum.SIMPLE;
      if (policy.modelPathMatcher) {
        this.computeModelPathMatcher(policy, false);
      } else {
        this.fetchModelsWithOptions();
      }
      if (!this.debouncedFetchModelsWithOptions) {
        this.debouncedFetchModelsWithOptions = debounce(this.fetchModelsWithOptions, 250)
      }
    },
    async computeModelPathMatcher(policy, forced) {
      if (this.modelPathMatcherMode == TriggerPolicyModelPathMatcherEnum.SIMPLE && policy.modelPathMatcher) {
        const modelPathMatcherParts = policy.modelPathMatcher.split("/")
        let data = undefined as unknown as any[];
        let slotModel = null;
        let slotModelTag = null;
        let search = null;
        let models = [] as any[];
        for(let i = 0; i < modelPathMatcherParts.length; i+=1 ) {
          if (i == 0 && modelPathMatcherParts[i].includes(":")) {
            this.modelsPage = 0;
            const result = await this.fetchModelsWithOptions(modelPathMatcherParts[i]);
            if (result.data.length > 0) {
              const model = result.data.find(model => model.json.instanceOf === modelPathMatcherParts[i])
              data = this.convertObjectIntoArray(model?.json.properties);
              slotModel = model;
              search = modelPathMatcherParts[i];
              models = this.models;
            }
          }
          if (i > 0) {
            if (!data || !data.length) {
              slotModel = null;
              search = null;
              break
            }
            const thisPart = data.find(
              (prop) => (
                (prop.type === "object" && !prop.deprecated && prop.name === modelPathMatcherParts[i]) || 
                (prop.type !== "object" && i === modelPathMatcherParts.length - 1 && prop.name === modelPathMatcherParts[i]))
            );
            if (thisPart) {
              if (thisPart.type !== "object") {
                slotModelTag = thisPart;
                search = modelPathMatcherParts.slice(0, modelPathMatcherParts.length - 1).join("/");
              } else {
                slotModel = thisPart;
                search = modelPathMatcherParts.join("/");
                models = data.filter((model) => model.type == "object" && !model.deprecated);
                data = this.convertObjectIntoArray(thisPart.properties)
              }
            } else {
              slotModel = null;
              search = null;
              break
            }
          }
        }

        this.models = models;
        this.searchDeviceModels = search;
        this.deviceSlotModel = slotModel;
        this.deviceSlotModelTag = slotModelTag;

        if (this.deviceSlotModel != null) {
          if (!this.deviceSlotModelTag && !forced) {
            // reenter the function in advanced mode
            this.modelPathMatcherMode = TriggerPolicyModelPathMatcherEnum.ADVANCED
            return await this.computeModelPathMatcher(policy, forced)
          } 
        } else if (!forced) {
          // change the mode and reenter the function
          this.modelPathMatcherMode = TriggerPolicyModelPathMatcherEnum.ADVANCED
          return await this.computeModelPathMatcher(policy, forced)
        } else {
          await this.fetchModelsWithOptions();
        }
      }
    },
    async toggleModelPathMatcher(mode: TriggerPolicyModelPathMatcherEnum) {
      this.modelPathMatcherMode = mode;
    },
    search: function (value, oldValue) {
      this.searchDeviceModels = value;
      if (!this.deviceSlotModel || value != this.deviceSlotModel.name+":"+this.deviceSlotModel.version) {
        if (this.debouncedFetchModelsWithOptions) {
          this.debouncedFetchModelsWithOptions(this.searchDeviceModels);
        }
      }
    },
    async reevaluateModelPathMatcher(value) {
      if (this.debouncedFetchModelsWithOptions) {
        this.debouncedFetchModelsWithOptions.cancel();
        this.debouncedFetchModelsWithOptions = null;
      }
      await this.computeModelPathMatcher(this.policy, true);
      if (!this.debouncedFetchModelsWithOptions) {
        this.debouncedFetchModelsWithOptions = debounce(this.fetchModelsWithOptions, 250)
      }
      this.$emit('modelPathMatcherMode', value);
    }
  },
  watch: {
    policy: function (n, o) {
      if (n.uuid != o.uuid) {
        this.initPolicy(n);
      }
    },
    deviceSlotModel: function (value, old_value) {
      if (!value) {
        this.deviceSlotModelTag = null;
        this.policy.modelPathMatcher = "";
      } else {
        const searches = this.searchDeviceModels?.split("/");
        if (searches?.length > 1) {
          searches.pop();
          searches.push(value.name);
          this.policy.modelPathMatcher = searches.join("/")
        } else {
          this.policy.modelPathMatcher = value.name + ":" + value.version;
        }
      }
    },
    deviceSlotModelTag: function (value) {
      this.$emit('deviceSlotModelTag', this.deviceSlotModelTag?.type)
      // set only valid policies, else preserved advanced model path
      if(this.deviceSlotModel) {
        if(this.deviceSlotModelTag) {
          this.policy.modelPathMatcher = this.searchDeviceModels + "/" + this.deviceSlotModelTag.name;
        }
      }
    },
    modelPathMatcherMode: function (value) {
      this.reevaluateModelPathMatcher(value);
    },
  },
  mounted() {
    if (!this.debouncedFetchModelsWithOptions) {
      this.debouncedFetchModelsWithOptions = debounce(this.fetchModelsWithOptions, 250)
    }
    if (this.policy) {
      this.initPolicy(this.policy);
    }
  },
});
</script>

<style scoped>
.header {
  font-weight: bold;
}

.radio-container {
  display: inline-flex;
  cursor: pointer;
}

.radio-text{
  margin: auto;
}
</style>

<style>
.trigger-model-path-matcher .v-field__field .v-field__input>input {
  top: 24px;
}
</style>
